import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image
} from "@react-pdf/renderer";
import { formatUSADate, formatUSATimestamp } from "../../../utils/dateFormatters";

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    color: "#333333",
    position: "relative"
  },
  watermark: {
    position: "absolute",
    top: "50%",
    left: "15%",
    width: "80%",
    opacity: 0.05
  },
  header: {
    marginBottom: 10
  },
  logo: {
    height: 30,
    objectFit: "contain",
    marginBottom: 5,
    width: 120
  },
  headerSeparator: {
    width: 150,
    height: 2,
    backgroundColor: "#333333",
    marginBottom: 5
  },
  locationName: {
    fontSize: 11,
    color: "#888888",
    marginBottom: 8
  },
  titleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    paddingBottom: 5,
    marginBottom: 10
  },
  reportTitle: {
    fontSize: 18,
    color: "#222222"
  },
  reportRangeText: {
    fontSize: 9,
    color: "#333"
  },
  graphHeading: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#333",
    padding: "10px 24px 5px"
  },
  infoGrid: {
    flexDirection: "row",
    marginBottom: 15
  },
  infoCol: {
    width: "50%"
  },
  fieldRow: {
    flexDirection: "row",
    marginBottom: 4
  },
  label: {
    width: 100,
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#666"
  },
  value: {
    fontSize: 9,
    color: "#000"
  },
  chartImage: {
    width: "100%",
    height: "auto",
    marginBottom: 10
  },
  table: {
    width: "100%",
    marginTop: 10
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f8f9fa",
    borderBottomWidth: 1,
    borderBottomColor: "#e9ecef",
    paddingVertical: 8,
    paddingHorizontal: 4
  },
  tableHeaderCell: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#333333"
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#f1f3f5"
  },
  tableCell: {
    fontSize: 9,
    color: "#333333"
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    borderTopWidth: 1,
    borderTopColor: "#eeeeee",
    paddingTop: 5,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 8,
    color: "#888"
  }
});

const getCategoryText = item => {
  const { type, reading } = item;
  if (!reading) return "-";
  let category =
    (type === "Blood Pressure"
      ? reading.bp_category
      : reading.heart_rate_category) || "-";
  if (category === "Stage 1") return "Stage 1 Hypertension";
  if (category === "Stage 2") return "Stage 2 Hypertension";
  return category;
};

const getRowColor = item => {
  const { isHeartRate, heart_rate_category, bp_category } = item;
  const cat =
    (isHeartRate ? heart_rate_category : bp_category)?.toLowerCase() || "";

  if (cat.includes("crisis")) return "#f3c7c7";
  if (cat.includes("hypo")) return "#d6e4ff";
  if (cat.includes("normal")) return "#d9f3ea";
  if (cat.includes("elevated")) return "#eef8d6";
  if (cat.includes("stage 1") || cat.includes("bradycardia")) return "#fde6bf";
  if (cat.includes("stage 2") || cat.includes("tachycardia")) return "#d4a19a";
  return "#ffffff";
};

const ReadingReportPDF = ({
  patient,
  readings,
  locationName,
  rangeLabel,
  logo,
  chartImage,
  readingCount
}) => {
  const flattenedReadings = [];
  readings?.forEach(reading => {
    let type = reading.reading_type_display || reading.reading_type;
    let val = "-";
    if (reading.reading_type === "blood_pressure")
      val = `${reading.systolic}/${reading.diastolic} mmHg`;
    else if (reading.reading_type === "weight") val = `${reading.weight} kg`;
    else if (reading.reading_type === "temperature")
      val = `${reading.temperature} °F`;
    else if (reading.reading_type === "blood_glucose")
      val = `${reading.glucose} mg/dL`;
    else if (reading.reading_type === "pulse_ox") val = `${reading.oxygen}%`;

    flattenedReadings.push({
      ...reading,
      reading,
      type,
      value: val,
      isHeartRate: false
    });
    if (reading.heart_rate) {
      flattenedReadings.push({
        ...reading,
        reading,
        type: "Heart Rate",
        value: `${reading.heart_rate} bpm`,
        isHeartRate: true
      });
    }
  });

  const patientName = `${patient?.user?.first_name || ""} ${
    patient?.user?.last_name || ""
  }`;
  const generationTime = formatUSATimestamp(new Date());

  return (
    <Document>
      {/* First Page with Header and Chart */}
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <View style={styles.headerSeparator} />
          <Text style={styles.locationName}>{locationName || "_"}</Text>
          <View style={styles.titleRow}>
            <Text style={styles.reportTitle}>Remote Monitoring Report</Text>
            <Text style={styles.reportRangeText}>{rangeLabel}</Text>
          </View>
        </View>

        <View style={styles.infoGrid}>
          <View style={styles.infoCol}>
            <View style={styles.fieldRow}>
              <Text style={styles.label}>Patient Name:</Text>
              <Text style={styles.value}>{patientName}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.label}>DOB:</Text>
              <Text style={styles.value}>
                {formatUSADate(patient?.user?.dob)}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.label}>Number of readings:</Text>
              <Text style={styles.value}>{readingCount}</Text>
            </View>
          </View>
          <View style={styles.infoCol}>
            <View style={styles.fieldRow}>
              <Text style={styles.label}>Doctor:</Text>
              <Text style={styles.value}>
                {patient?.provider?.first_name} {patient?.provider?.last_name}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.label}>Subaccount Name:</Text>
              <Text style={styles.value}>{locationName || "_"}</Text>
            </View>
          </View>
        </View>

        <Text style={styles.graphHeading}>RPM Graph</Text>
        {chartImage && <Image src={chartImage} style={styles.chartImage} />}

        {/* Watermark - placed last and fixed */}
        <Image src={logo} style={styles.watermark} fixed />

        {/* Footer - placed last and fixed */}
        <View style={styles.footer} fixed>
          <Text>{patientName} - Remote Monitoring Report</Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `${pageNumber} of ${totalPages}`
            }
          />
          <Text>{generationTime}</Text>
        </View>
      </Page>

      {/* Second Page with All Table Data - Let it flow naturally across pages */}
      <Page size="A4" style={styles.page}>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, { width: "30%" }]}>
              Timestamp
            </Text>
            <Text style={[styles.tableHeaderCell, { width: "20%" }]}>Type</Text>
            <Text style={[styles.tableHeaderCell, { width: "20%" }]}>
              Reading
            </Text>
            <Text style={[styles.tableHeaderCell, { width: "30%" }]}>
              Category
            </Text>
          </View>
          {flattenedReadings.map((item, i) => (
            <View
              key={i}
              style={[styles.tableRow, { backgroundColor: getRowColor(item) }]}
              wrap={false}
            >
              <Text style={[styles.tableCell, { width: "30%" }]}>
                {formatUSATimestamp(item.recorded_at)}
              </Text>
              <Text style={[styles.tableCell, { width: "20%" }]}>
                {item.type}
              </Text>
              <Text style={[styles.tableCell, { width: "20%" }]}>
                {item.value}
              </Text>
              <Text style={[styles.tableCell, { width: "30%" }]}>
                {getCategoryText(item)}
              </Text>
            </View>
          ))}
        </View>

        {/* Watermark - placed last and fixed */}
        <Image src={logo} style={styles.watermark} fixed />

        {/* Footer - placed last and fixed */}
        <View style={styles.footer} fixed>
          <Text>{patientName} - Remote Monitoring Report</Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `${pageNumber} of ${totalPages}`
            }
          />
          <Text>{generationTime}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default ReadingReportPDF;
